import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Inbox, Check, X, DoorOpen, DoorClosed, Loader2 } from 'lucide-react';
import { api, parseApiError } from '../lib/api';
import { cn } from '../lib/cn';

export function ClanApplicationsPanel({
  clanName,
  publicApplicationsOpen,
  className,
}: {
  clanName: string;
  publicApplicationsOpen: boolean;
  className?: string;
}) {
  const qc = useQueryClient();
  const [optimisticOpen, setOptimisticOpen] = useState<boolean | null>(null);
  const [decideError, setDecideError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['clanApplications', clanName],
    queryFn: () => api.listClanApplications(clanName),
  });

  const effectiveOpen = optimisticOpen ?? publicApplicationsOpen;

  const toggleMut = useMutation({
    mutationFn: (next: boolean) => api.updateClanMeta(clanName, { publicApplicationsOpen: next }),
    onMutate: next => setOptimisticOpen(next),
    onSuccess: () => {
      // Invalidate ALL clan queries — the parent page keys its clan query by
      // me.clanName (the canonical slug, e.g. HAMMER), which differs in case from
      // this panel's clanName prop (the display name, e.g. Hammer). A
      // case-specific key would miss the parent and the toggle would snap back.
      qc.invalidateQueries({ queryKey: ['clan'] });
    },
    onError: () => setOptimisticOpen(null),
  });

  const decideMut = useMutation({
    mutationFn: ({ applicantId, decision }: { applicantId: string; decision: 'ACCEPTED' | 'REJECTED' }) =>
      api.decideClanApplication(clanName, applicantId, decision),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clanApplications', clanName] });
      qc.invalidateQueries({ queryKey: ['clan', clanName] });
      setDecideError(null);
    },
    onError: (e: Error) => setDecideError(parseApiError(e).message),
  });

  // Clear the optimistic flag once the refreshed prop catches up — avoids a flicker.
  useEffect(() => {
    if (optimisticOpen !== null && optimisticOpen === publicApplicationsOpen) setOptimisticOpen(null);
  }, [publicApplicationsOpen, optimisticOpen]);

  const pending = (data?.applications ?? []).filter(a => a.status === 'PENDING');

  return (
    <div className={cn('rounded-xl border border-forge-900/50 bg-paper-elevated p-5 shadow-forged', className)}>
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Inbox className="h-4 w-4 text-forge-400" strokeWidth={2.5} />
          <h2 className="font-display text-[11px] font-black uppercase tracking-[0.22em] text-forge-300">
            Applications
          </h2>
          {pending.length > 0 && (
            <span className="border border-amber-500/40 bg-amber-500/10 px-1.5 py-0 font-display text-[9px] font-black uppercase tracking-wider text-amber-300">
              {pending.length} pending
            </span>
          )}
        </div>

        <button
          onClick={() => toggleMut.mutate(!effectiveOpen)}
          disabled={toggleMut.isPending}
          className={cn(
            'flex items-center gap-1.5 border px-2.5 py-1.5 font-display text-[10px] font-black uppercase tracking-wider transition-all disabled:opacity-60',
            effectiveOpen
              ? 'border-emerald-500/60 bg-emerald-950/40 text-emerald-200 hover:border-emerald-400 hover:bg-emerald-900/50 hover:text-white'
              : 'border-iron-700 bg-iron-950 text-iron-300 hover:border-iron-500 hover:text-white'
          )}
        >
          {toggleMut.isPending ? (
            <Loader2 className="h-3 w-3 animate-spin" strokeWidth={2.5} />
          ) : effectiveOpen ? (
            <DoorOpen className="h-3 w-3" strokeWidth={2.5} />
          ) : (
            <DoorClosed className="h-3 w-3" strokeWidth={2.5} />
          )}
          {effectiveOpen ? 'Open to public' : 'Closed'}
        </button>
      </div>

      <p className="mb-4 text-xs text-iron-400">
        {effectiveOpen
          ? 'Players can find this clan in Browse Clans and submit applications. Approve or reject below.'
          : 'Clan is hidden from Browse Clans and not accepting new applications.'}
      </p>

      {decideError && (
        <p className="mb-3 text-xs text-red-400">{decideError}</p>
      )}

      {isLoading ? (
        <p className="text-sm text-iron-500">Loading applications…</p>
      ) : pending.length === 0 ? (
        <p className="text-sm text-iron-500">No pending applications.</p>
      ) : (
        <div className="space-y-2">
          {pending.map(app => (
            <div
              key={app.applicantDiscordId}
              className="border border-iron-800 bg-iron-950 p-3"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="font-display text-sm font-black uppercase tracking-wide text-white">
                    {app.applicantGamertag ?? <span className="italic text-iron-400">No gamertag supplied</span>}
                  </p>
                  <p className="mt-0.5 font-mono text-[10px] text-iron-500">
                    {app.applicantDiscordId} · {new Date(app.createdAt).toLocaleDateString()}
                  </p>
                  {app.message && (
                    <p className="mt-2 whitespace-pre-line text-xs text-iron-300">{app.message}</p>
                  )}
                </div>

                <div className="flex shrink-0 items-center gap-1.5">
                  <button
                    onClick={() => decideMut.mutate({ applicantId: app.applicantDiscordId, decision: 'ACCEPTED' })}
                    disabled={decideMut.isPending}
                    className="flex h-8 items-center gap-1 border border-emerald-500/50 bg-emerald-950/40 px-2.5 font-display text-[10px] font-black uppercase tracking-wider text-emerald-200 transition-all hover:border-emerald-400 hover:bg-emerald-900/50 hover:text-white disabled:opacity-40"
                  >
                    <Check className="h-3 w-3" strokeWidth={3} />
                    Accept
                  </button>
                  <button
                    onClick={() => decideMut.mutate({ applicantId: app.applicantDiscordId, decision: 'REJECTED' })}
                    disabled={decideMut.isPending}
                    className="flex h-8 items-center gap-1 border border-red-500/50 bg-red-950/40 px-2.5 font-display text-[10px] font-black uppercase tracking-wider text-red-200 transition-all hover:border-red-400 hover:bg-red-900/50 hover:text-white disabled:opacity-40"
                  >
                    <X className="h-3 w-3" strokeWidth={3} />
                    Reject
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
